import type { CanvasTarget, VoicePreviewPayload } from './types';

const text = (value: unknown, max: number) =>
  typeof value === 'string' && value.trim().length > 0 && value.length <= max;

function validTable(
  value: Record<string, unknown>,
): value is Extract<VoicePreviewPayload, { columns: string[] }> {
  if (value.kind !== 'table' && value.kind !== 'comparison') return false;
  if (
    !text(value.title, 200) ||
    !Array.isArray(value.columns) ||
    !value.columns.length ||
    value.columns.length > 16
  )
    return false;
  const columns = value.columns;
  if (
    !columns.every((column) => text(column, 80)) ||
    !Array.isArray(value.rows) ||
    !value.rows.length ||
    value.rows.length > 100
  )
    return false;
  return value.rows.every(
    (row) =>
      Array.isArray(row) &&
      row.length === columns.length &&
      row.length <= 16 &&
      row.every((cell) => typeof cell === 'string' && cell.length <= 500),
  );
}

function validDocument(
  value: Record<string, unknown>,
): value is Extract<VoicePreviewPayload, { markdown: string }> {
  return (
    (value.kind === 'result' || value.kind === 'draft') &&
    text(value.title, 200) &&
    text(value.markdown, 20_000)
  );
}

export function voicePreviewTarget(
  value: unknown,
  threadId: string,
  activeThreadId: string,
  resumed: boolean,
): CanvasTarget | null {
  if (resumed || threadId !== activeThreadId || !value || typeof value !== 'object') return null;
  const envelope = value as {
    version?: unknown;
    channel?: unknown;
    messageId?: unknown;
    part?: { sequence?: unknown; visual?: unknown };
  };
  if (envelope.version !== 1 || envelope.channel !== 'spoken') return null;
  const spoken = envelope;
  const part = spoken.part;
  if (
    !part ||
    typeof part.sequence !== 'number' ||
    !Number.isInteger(part.sequence) ||
    part.sequence < 0 ||
    part.sequence > 10_000
  )
    return null;
  if (!part.visual || typeof part.visual !== 'object') return null;
  const preview = part.visual as Record<string, unknown>;
  if (!validTable(preview) && !validDocument(preview)) return null;
  if (
    typeof spoken.messageId !== 'string' ||
    !spoken.messageId.trim() ||
    spoken.messageId.length > 128
  )
    return null;
  const messageId = spoken.messageId;
  return {
    kind: 'voice-preview',
    id: `${threadId}:${messageId}:${part.sequence}`,
    threadId,
    preview: preview as VoicePreviewPayload,
  };
}
